import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import Layout from "./layout/Layout.jsx";

// Lazy load route components
const Home = React.lazy(() => import("./pages/Home.jsx"));
const Chat = React.lazy(() => import("./pages/Chat.jsx"));
const MainDataPage = React.lazy(() => import("./pages/MainDataPage.jsx"));
const BucketView = React.lazy(() => import("./pages/BucketView.jsx"));
const ViewByRetailer = React.lazy(() => import("./pages/ViewByRetailer.jsx"));
const ViewByPartNumber = React.lazy(() => import("./pages/ViewByPartNumber.jsx"));
const DetailedTransactionView = React.lazy(() => import("./pages/DetailedTransactionView.jsx"));
const LoginSignup = React.lazy(() => import("./pages/LoginSignup.jsx"));
const DataInsights = React.lazy(() => import("./pages/DataInsights.jsx"));
const AIShowcase = React.lazy(() => import("./website/AIShowcase.jsx"));
const PrivacyPolicy = React.lazy(() => import("./common/PrivacyPolicy.jsx"));
const TermsAndConditions = React.lazy(() => import("./common/TermsAndConditions.jsx"));

// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div className="loading">
    <div className="loading-spinner"></div>
  </div>
);

// ScrollToTop component to ensure page scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  // Check if the current path is any website related route (including root)
  const isWebsitePage = location.pathname === "/" ||
                       location.pathname === "/ai-features" || 
                       location.pathname === "/website" ||
                       location.pathname.startsWith("/website/") ||
                       location.pathname === "/privacy-policy" ||
                       location.pathname === "/terms";

  // If it's the login page or any website page, render without the Layout
  if (isLoginPage || isWebsitePage) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route 
            path="/login" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <LoginSignup />
              </Suspense>
            } 
          />
          {/* Root path shows the website page */}
          <Route 
            path="/" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase />
              </Suspense>
            } 
          />
          {/* Handle all website related routes in this section */}
          <Route 
            path="/ai-features" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase />
              </Suspense>
            } 
          />
          <Route 
            path="/privacy-policy"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />
          <Route 
            path="/terms"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TermsAndConditions />
              </Suspense>
            }
          />
          <Route 
            path="/website" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase />
              </Suspense>
            } 
          />
          <Route 
            path="/website/features" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase section="features" />
              </Suspense>
            } 
          />
          <Route 
            path="/website/pricing" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase section="pricing" />
              </Suspense>
            } 
          />
          <Route 
            path="/website/about" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AIShowcase section="about" />
              </Suspense>
            } 
          />
        </Routes>
      </>
    );
  }

  // Otherwise, use the regular app Layout with protected routes
  return (
    <div className="app-container">
      <ScrollToTop />
      <Layout>
        <Routes>
          {/* Protected Routes - Home moved to /app */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <Home />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <Chat />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <MainDataPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bucket-view"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <BucketView />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/view-by-retailer"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <ViewByRetailer />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/view-by-part-number"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <ViewByPartNumber />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/detailed-transaction-view"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <DetailedTransactionView />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-insights"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <DataInsights />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Layout>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;